import { useEffect, useRef } from 'react';
import { useStore } from '../state/store';
import type { Aircraft, Bbox, SourceHealth } from '../types';
import { wsUrl } from '../api';

interface ServerMessage {
  type: 'snapshot' | 'update' | 'error' | 'pong';
  ts?: number;
  aircraft?: Aircraft[];
  removed?: string[];
  sources?: SourceHealth[];
  error?: string;
  /** Provisional best-effort snapshot sent immediately on subscribe, before the
   *  real (possibly cold-starting) watcher's first poll resolves. Merged in
   *  like an update rather than replacing the aircraft set outright. */
  stale?: boolean;
}

/**
 * WebSocket client with automatic reconnect + resubscribe. Subscribes to the
 * given bbox and streams snapshots/updates into the zustand store. The
 * socket itself is opened once and kept alive across bbox changes (e.g. from
 * "follow view" panning) — only a new `subscribe` message is sent on the
 * existing connection, rather than tearing down and reconnecting.
 *
 * `fullTrail` mirrors the "full trail" map-layer toggle: it's sent on every
 * subscribe so the server's snapshot includes each aircraft's whole retained
 * history (up to its own retention window) instead of the short recent
 * window sent by default — otherwise the trail could only ever grow from
 * whenever this tab happened to connect, never showing where the aircraft
 * actually came from.
 */
export function useLiveTraffic(bbox: Bbox | null, fullTrail = false): void {
  const applySnapshot = useStore((s) => s.applySnapshot);
  const applyUpdate = useStore((s) => s.applyUpdate);
  const setConnection = useStore((s) => s.setConnection);
  const bboxRef = useRef(bbox);
  bboxRef.current = bbox;
  const fullTrailRef = useRef(fullTrail);
  fullTrailRef.current = fullTrail;
  const resubscribeRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!bbox) return;
    let ws: WebSocket | null = null;
    let closed = false;
    let attempt = 0;
    let reconnectTimer: number | undefined;

    const sendSubscribe = () => {
      if (ws && ws.readyState === WebSocket.OPEN && bboxRef.current) {
        ws.send(JSON.stringify({ type: 'subscribe', bbox: bboxRef.current, fullTrail: fullTrailRef.current }));
      }
    };
    resubscribeRef.current = sendSubscribe;

    const connect = () => {
      if (closed) return;
      setConnection(attempt === 0 ? 'connecting' : 'reconnecting');
      ws = new WebSocket(wsUrl());

      ws.onopen = () => {
        attempt = 0;
        setConnection('connected');
        sendSubscribe();
      };

      ws.onmessage = (e) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(e.data as string);
        } catch {
          return;
        }
        if (msg.type === 'snapshot' && msg.stale) {
          // Provisional: merge in without replacing/clearing anything, and
          // skip outright when empty so it can never blank an already-
          // populated map while the real snapshot is still in flight.
          if (msg.aircraft && msg.aircraft.length > 0) {
            applyUpdate(msg.aircraft, [], msg.sources ?? [], msg.ts ?? Date.now());
          }
        } else if (msg.type === 'snapshot') {
          applySnapshot(msg.aircraft ?? [], msg.sources ?? [], msg.ts ?? Date.now());
        } else if (msg.type === 'update') {
          applyUpdate(msg.aircraft ?? [], msg.removed ?? [], msg.sources ?? [], msg.ts ?? Date.now());
        }
      };

      ws.onclose = () => {
        if (closed) return;
        setConnection('reconnecting');
        attempt += 1;
        const delay = Math.min(1000 * 2 ** attempt, 15_000);
        reconnectTimer = window.setTimeout(connect, delay);
      };

      ws.onerror = () => ws?.close();
    };

    connect();
    return () => {
      closed = true;
      window.clearTimeout(reconnectTimer);
      resubscribeRef.current = () => {};
      ws?.close();
    };
    // Only (re)create the socket on mount / when a bbox first becomes available —
    // subsequent bbox content changes resubscribe on the existing connection below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox === null]);

  useEffect(() => {
    if (!bbox) return;
    resubscribeRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbox && `${bbox.lamin},${bbox.lomin},${bbox.lamax},${bbox.lomax}`]);

  // Toggling "full trail" on needs its own resubscribe (not just a future
  // subscribe) so the deeper history backfills immediately instead of only
  // starting to accumulate from the moment the toggle was flipped.
  useEffect(() => {
    if (!bbox) return;
    resubscribeRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullTrail]);
}
